/**
 * image-sizes.ts — every photo the website shows is served at a size that suits the screen showing it.
 *
 * Jacky, 25 Oct 2026: "website display image should all be at suitable size for webpage" — "use intelligent
 * setting for website, not a static". The product photos in the `product-images` blob are 1,000–1,600 px
 * (35–250 KB) and were sent as they are, even into a 28 px colour square: a fully scrolled Price my floor
 * downloaded 650 images, ~149 MB.
 *
 * Now a photo is written with a `srcset` of widths from one ladder and a `sizes` hint, so the BROWSER picks
 * the smallest width that is sharp at its own layout width and pixel density. Each width is made on demand by
 * `/img/<width>/<source>/<path>` (pages/img/[w]/[...path].ts) and kept after the first request
 * (lib/image-cache.ts). No fixed thumbnails, so a photo added tomorrow is sized the moment it is first shown.
 *
 * Two sources only — never an open proxy: `b/` = the product-images blob, `s/` = the site's own
 * `/images/site/` photos. Any other URL (logos, SVGs, document pages, another host) is left exactly as it is.
 *
 * Pure: no Astro, no env, so node --test imports it and the React islands share it.
 */

export const PRODUCT_IMAGES_BASE = "https://artemisflooring.blob.core.windows.net/product-images/";
/** Browser URLs change as well as blob keys: old responses were cached for seven days. */
export const IMAGE_RENDER_REVISION = "detail-2";
const SITE_IMAGES = "/images/site/";

/** The width ladder. Few steps on purpose: each step is one stored copy per photo. */
export const IMG_WIDTHS = [64, 128, 256, 384, 640, 960, 1280, 1600] as const;
export type ImgWidth = (typeof IMG_WIDTHS)[number];
const MAX: ImgWidth = 1600;

/** The smallest ladder width at least `w` (the top step for anything larger). */
export const snapWidth = (w: number): ImgWidth => IMG_WIDTHS.find((x) => x >= w) ?? MAX;

export interface ImgSource { kind: "b" | "s"; path: string }

/** Which source a URL is, with its path still URL-encoded as it appears; null = not ours to size. */
export function imgSource(url: string | null | undefined): ImgSource | null {
  if (!url) return null;
  if (url.startsWith(PRODUCT_IMAGES_BASE)) { const path = url.slice(PRODUCT_IMAGES_BASE.length).split(/[?#]/)[0] ?? ""; return safePath(path) ? { kind: "b", path } : null; }
  if (url.startsWith(SITE_IMAGES)) { const path = url.slice(SITE_IMAGES.length).split(/[?#]/)[0] ?? ""; return safePath(path) ? { kind: "s", path } : null; }
  return null;
}

/** A path we will fetch: relative, no "..", no backslash, an image extension. */
function safePath(path: string): boolean {
  let decoded: string;
  try { decoded = decodeURIComponent(path); } catch { return false; }
  return !!decoded && !decoded.startsWith("/") && !decoded.includes("\\") && !decoded.split("/").includes("..")
    && /\.(webp|jpe?g|png|avif)$/i.test(decoded);
}

/** The sized address of a photo at one ladder width; any other URL comes back unchanged. */
export function sizedUrl(url: string, width: number): string {
  const s = imgSource(url);
  return s ? `/img/${snapWidth(width)}/${s.kind}/${s.path}?v=${IMAGE_RENDER_REVISION}` : url;
}

/**
 * `src` + `srcSet` for a photo laid out at most `cssWidth` CSS pixels wide (and at least `minCssWidth`, for a
 * photo that shrinks on a phone — a full-width hero is 375 on a phone and 1,600 on a desktop). The set runs from
 * 1× the smallest to 3× the largest layout width, so a 3× phone and a 1× desktop each take what they need; `src`
 * (2× the largest) is the fallback. Pair it with a `sizes` attribute that says how wide the photo is laid out.
 * A URL that is not ours gets `{ src: url }` only.
 */
export function responsiveImg(url: string | null | undefined, cssWidth: number, minCssWidth = cssWidth): { src: string; srcSet?: string } {
  if (!url) return { src: "" };
  const s = imgSource(url);
  if (!s) return { src: url };
  const lo = snapWidth(Math.min(minCssWidth, cssWidth)), hi = snapWidth(cssWidth * 3);
  const widths = IMG_WIDTHS.filter((w) => w >= lo && w <= hi);
  return { src: sizedUrl(url, cssWidth * 2), srcSet: widths.map((w) => `/img/${w}/${s.kind}/${s.path}?v=${IMAGE_RENDER_REVISION} ${w}w`).join(", ") };
}

/** The route's side: the width and source a request names, or null for anything we do not serve. */
export function parseImgRequest(w: string | undefined, rest: string | undefined): { width: ImgWidth; source: ImgSource } | null {
  const width = Number(w);
  if (!(IMG_WIDTHS as readonly number[]).includes(width) || !rest) return null;
  const i = rest.indexOf("/");
  const kind = rest.slice(0, i), path = rest.slice(i + 1);
  if (i < 0 || (kind !== "b" && kind !== "s") || !safePath(path)) return null;
  return { width: width as ImgWidth, source: { kind, path } };
}

/** AVIF when the browser says it takes it (smaller), else WebP — both are what every current browser shows. */
export const pickFormat = (accept: string | null | undefined): "avif" | "webp" => (/image\/avif/i.test(accept ?? "") ? "avif" : "webp");

/** Where the original lives. A site photo is fetched from the site's own origin. */
export const originalUrl = (s: ImgSource, origin: string): string =>
  s.kind === "b" ? PRODUCT_IMAGES_BASE + s.path : new URL(SITE_IMAGES + s.path, origin).toString();

/** The stored copy's name: tied to the original's version (ETag), so a replaced photo is sized again. */
export function cacheKey(s: ImgSource, version: string, width: ImgWidth, format: "avif" | "webp", profile = IMAGE_RENDER_REVISION): string {
  const v = version.replace(/[^A-Za-z0-9]/g, "").slice(0, 40) || "v0";
  return `${profile}/${s.kind}/${width}/${format}/${v}/${s.path}`;
}
